// Authentication Helper for Stremio

import { AuthStatus } from './types';
import { showToast, validateAuthKey, copyToClipboard, setupPasswordToggle } from './utils';
import { debugConsole } from './debug';

// Callback to be set by main.ts - called when auth succeeds
let onAuthSuccessCallback: ((authKey: string) => void) | null = null;

/**
 * Set callback to be called when authentication succeeds
 * This breaks the circular dependency between main.ts and auth.ts
 */
export function setOnAuthSuccessCallback(callback: (authKey: string) => void): void {
  onAuthSuccessCallback = callback;
  debugConsole.log('[Auth] Auth success callback registered');
}

/**
 * Fetch auth key from Stremio API using email/password
 */
export async function fetchAuthKey(email: string, password: string): Promise<string | null> {
  debugConsole.log(`Attempting to fetch auth key for ${email}`);

  try {
    const response = await fetch('https://api.strem.io/api/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'Login',
        email,
        password,
      }),
    });

    if (!response.ok) {
      debugConsole.log(`Login failed: HTTP ${response.status}`);
      throw new Error(`Login failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.result && data.result.authKey) {
      debugConsole.log('Auth key fetched successfully');
      return data.result.authKey;
    }

    debugConsole.log('No auth key in response');
    throw new Error('No auth key in response');
  } catch (error) {
    debugConsole.log(`Error fetching auth key: ${error}`);
    throw error;
  }
}

/**
 * Validate auth key by attempting to fetch library metadata
 */
export async function validateAuthKeyWithAPI(authKey: string): Promise<AuthStatus> {
  debugConsole.log('Validating auth key with Stremio API');

  try {
    const response = await fetch('https://api.strem.io/api/datastoreMeta', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        authKey,
        collection: 'libraryItem',
      }),
    });

    if (!response.ok) {
      debugConsole.log(`Auth validation failed: HTTP ${response.status}`);
      return {
        authenticated: false,
        error: 'Invalid auth key',
      };
    }

    const data = await response.json();

    // Log the full response for debugging
    debugConsole.log('=== Stremio API Response ===');
    debugConsole.log('Response keys: ' + Object.keys(data).join(', '));

    if (data.user) {
      debugConsole.log('data.user keys: ' + Object.keys(data.user).join(', '));
      debugConsole.log('data.user.email: ' + data.user.email);
      debugConsole.log('data.user._id: ' + data.user._id);
    }

    if (data.result) {
      debugConsole.log('data.result exists');
      if (data.result.user) {
        debugConsole.log('data.result.user keys: ' + Object.keys(data.result.user).join(', '));
        debugConsole.log('data.result.user.email: ' + data.result.user.email);
      }
    }

    // Check for user email in response
    if (data.user && data.user.email) {
      debugConsole.log(`✅ Auth validated for ${data.user.email}`);
      return {
        authenticated: true,
        email: data.user.email,
      };
    }

    // Check alternative structure (some API endpoints return it differently)
    if (data.result && data.result.user && data.result.user.email) {
      debugConsole.log(`✅ Auth validated for ${data.result.user.email}`);
      return {
        authenticated: true,
        email: data.result.user.email,
      };
    }

    debugConsole.log('⚠️ Auth validated but no user email found in response');
    return {
      authenticated: true,
    };
  } catch (error) {
    debugConsole.log(`Error validating auth: ${error}`);
    return {
      authenticated: false,
      error: String(error),
    };
  }
}

/**
 * Setup auth UI event listeners
 */
export function setupAuthUI(): void {
  const preAuthDiv = document.getElementById('pre-auth');
  const postAuthDiv = document.getElementById('post-auth');
  const authKeyInput = document.getElementById('authKey') as HTMLInputElement;
  const authKeyDisplayInput = document.getElementById('authKeyDisplay') as HTMLInputElement;
  const showAuthHelperBtn = document.getElementById('showAuthHelper');
  const authHelperDiv = document.getElementById('authHelper');
  const emailInput = document.getElementById('email') as HTMLInputElement;
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const fetchAuthKeyBtn = document.getElementById('fetchAuthKey');
  const loginWithAuthKeyBtn = document.getElementById('loginWithAuthKey');
  const changeAuthBtn = document.getElementById('changeAuth');
  const logoutBtn = document.getElementById('logoutBtn');
  const copyAuthKeyBtn = document.getElementById('copyAuthKey');
  const userEmailSpan = document.getElementById('userEmail');

  // Setup password toggle for auth key display
  setupPasswordToggle('authKeyDisplay', 'toggleAuthKeyDisplay');

  // Login with auth key button
  if (loginWithAuthKeyBtn && authKeyInput) {
    loginWithAuthKeyBtn.addEventListener('click', async () => {
      const authKey = authKeyInput.value.trim();

      if (!authKey) {
        showToast('Please enter your auth key', 'error');
        return;
      }

      const validation = validateAuthKey(authKey);
      if (!validation.valid) {
        showToast(validation.message || 'Invalid auth key', 'error');
        return;
      }

      loginWithAuthKeyBtn.textContent = 'Validating...';
      loginWithAuthKeyBtn.setAttribute('disabled', 'true');

      try {
        const status = await validateAuthKeyWithAPI(authKey);
        if (status.authenticated) {
          showToast('Successfully authenticated!', 'success');
          showPostAuthState(status.email, authKey);
        } else {
          showToast(status.error || 'Authentication failed', 'error');
        }
      } catch (error) {
        showToast(`Failed to validate auth key: ${error}`, 'error');
      } finally {
        loginWithAuthKeyBtn.textContent = 'Login';
        loginWithAuthKeyBtn.removeAttribute('disabled');
      }
    });
  }

  // Toggle auth helper visibility
  if (showAuthHelperBtn && authHelperDiv) {
    showAuthHelperBtn.addEventListener('click', () => {
      const isHidden = authHelperDiv.style.display === 'none';
      authHelperDiv.style.display = isHidden ? 'block' : 'none';
      showAuthHelperBtn.textContent = isHidden
        ? 'Hide auth helper'
        : 'Need help getting your auth key?';
    });
  }

  // Fetch auth key
  if (fetchAuthKeyBtn) {
    fetchAuthKeyBtn.addEventListener('click', async () => {
      const email = emailInput?.value.trim();
      const password = passwordInput?.value;

      if (!email || !password) {
        showToast('Please enter email and password', 'error');
        return;
      }

      fetchAuthKeyBtn.textContent = 'Fetching...';
      fetchAuthKeyBtn.setAttribute('disabled', 'true');

      try {
        const authKey = await fetchAuthKey(email, password);

        if (authKey && authKeyInput) {
          authKeyInput.value = authKey;
          showToast('✅ Auth key fetched successfully!', 'success');

          // Hide helper
          if (authHelperDiv) {
            authHelperDiv.style.display = 'none';
          }

          // Clear password
          if (passwordInput) {
            passwordInput.value = '';
          }

          // Show post-auth state with auth key
          showPostAuthState(email, authKey);
        }
      } catch (error) {
        showToast(`Failed to fetch auth key: ${error}`, 'error');
      } finally {
        fetchAuthKeyBtn.textContent = 'Get Auth Key';
        fetchAuthKeyBtn.removeAttribute('disabled');
      }
    });
  }

  // Copy auth key button
  if (copyAuthKeyBtn && authKeyDisplayInput) {
    copyAuthKeyBtn.addEventListener('click', async () => {
      const success = await copyToClipboard(authKeyDisplayInput.value);
      if (success) {
        showToast('Auth key copied to clipboard!', 'success');
      } else {
        showToast('Failed to copy auth key', 'error');
      }
    });
  }

  // Change authentication
  if (changeAuthBtn) {
    changeAuthBtn.addEventListener('click', () => {
      if (preAuthDiv) preAuthDiv.style.display = 'block';
      if (postAuthDiv) postAuthDiv.style.display = 'none';
      if (authKeyInput) authKeyInput.value = '';
      if (emailInput) emailInput.value = '';
      if (passwordInput) passwordInput.value = '';

      // Reset auth helper state
      if (authHelperDiv) {
        authHelperDiv.style.display = 'none';
      }
      if (showAuthHelperBtn) {
        showAuthHelperBtn.textContent = 'Need help getting your auth key?';
      }

      debugConsole.log('Auth cleared, returning to pre-auth state');
    });
  }

  // Logout button - completely resets page
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      // Reload the page to go back to fresh /configure state
      window.location.href = '/configure';
    });
  }

  // Show post-auth state helper
  function showPostAuthState(email?: string, authKey?: string): void {
    if (preAuthDiv) preAuthDiv.style.display = 'none';
    if (postAuthDiv) postAuthDiv.style.display = 'block';
    if (userEmailSpan) {
      if (email) {
        userEmailSpan.textContent = email;
        debugConsole.log(`[Auth] Set user email to: ${email}`);
      } else {
        userEmailSpan.textContent = 'Authenticated User';
        debugConsole.log('[Auth] No email provided, using default text');
      }
    }
    if (authKeyDisplayInput && authKey) {
      authKeyDisplayInput.value = authKey;
    } else if (authKeyDisplayInput && authKeyInput) {
      authKeyDisplayInput.value = authKeyInput.value;
    }
    debugConsole.log('[Auth] Switched to post-auth state');

    // CRITICAL: Call callback to update global config and initialize previews
    const finalAuthKey = authKey || authKeyInput?.value || '';
    if (finalAuthKey) {
      debugConsole.log('[Auth] Calling auth success callback with auth key');
      if (onAuthSuccessCallback) {
        onAuthSuccessCallback(finalAuthKey);
      } else {
        debugConsole.log('[Auth] ERROR: Auth success callback not registered!');
      }
    } else {
      debugConsole.log('[Auth] Warning: No auth key available to pass to callback');
    }
  }

  // Auto-validate on load if auth key present
  if (authKeyInput && authKeyInput.value.trim()) {
    const validation = validateAuthKey(authKeyInput.value);
    if (validation.valid) {
      validateAuthKeyWithAPI(authKeyInput.value).then((status) => {
        if (status.authenticated) {
          showPostAuthState(status.email, authKeyInput.value);
        }
      });
    }
  }
}
